import { useEffect } from 'react';
import { useExpertOrders } from '../../../hooks/expertDashboardHooks/expertOrderHooks/useExpertOrders';
import { Loader2,  Clock, MessageSquare, Phone, Video, CreditCard, Filter, RefreshCw, Layers, CalendarDays } from 'lucide-react';

export default function ExpertOrdersPage() {
  const {
    orders,
    totalOrders,
    isLoadingOrders,
    filters,
    setFilters,
    fetchOrders
  } = useExpertOrders();

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.sessionType, filters.startDate, filters.endDate, filters.month, filters.year]);

  const handleTabChange = (type: 'all' | 'chat' | 'call' | 'video') => {
    setFilters(prev => ({ ...prev, page: 1, sessionType: type }));
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, page: 1, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters(prev => ({
      ...prev,
      page: 1,
      startDate: '',
      endDate: '',
      month: '',
      year: ''
    }));
  };

  const formatDuration = (totalSeconds: number) => {
    if (!totalSeconds) return '0s';
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getSessionIcon = (type: string) => {
    switch (type) {
      case 'chat': return <MessageSquare className="w-3.5 h-3.5 text-blue-500" />;
      case 'call': return <Phone className="w-3.5 h-3.5 text-emerald-500" />;
      case 'video': return <Video className="w-3.5 h-3.5 text-purple-500" />;
      default: return <CreditCard className="w-3.5 h-3.5 text-slate-400" />;
    }
  };

  const monthsList = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const yearsList = ["2024", "2025", "2026", "2027"];

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-7 min-h-screen bg-[#f8fafc] font-sans antialiased text-slate-600">
      
      {/* PREMIUM HEADER MATRIX */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-6 rounded-2xl border border-slate-200/60 shadow-[0_2px_8px_rgba(15,23,42,0.02)]">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-xl border border-blue-100 shadow-sm">
              <Layers className="w-5 h-5" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Consultation Orders</h1>
          </div>
          <p className="text-sm text-slate-400 mt-1.5 font-medium">Track comprehensive transactional earnings breakdown records per consultation session.</p>
        </div>
        <div className="bg-gradient-to-b from-slate-50 to-slate-100/50 px-6 py-3.5 rounded-xl border border-slate-200/80 text-right shrink-0 shadow-inner">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total Completed Orders</p>
          <p className="text-2xl font-black text-slate-800 mt-0.5 tracking-tight">{totalOrders}</p>
        </div>
      </div>

      {/* 🟢 RE-DESIGNED: SaaS ADVANCED LEDGER SORTING CONTROLS */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-[0_4px_20px_rgba(15,23,42,0.03)] overflow-hidden transition-all duration-300">
        
        {/* Filter Title Header Block */}
        <div className="px-5 py-4 bg-slate-50/60 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-800 font-bold text-sm tracking-tight">
            <Filter className="w-4 h-4 text-slate-400" />
            <h2>Advance Filters</h2>
          </div>
          {/* Active indicator status anchor node */}
          {(!!filters.startDate || !!filters.endDate || !!filters.month) && (
            <span className="w-2 h-2 bg-blue-500 rounded-full animate-ping"></span>
          )}
        </div>
        
        {/* Controls Layout Structure Grid Row */}
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 items-end text-xs">
          
          {/* Start Date */}
          <div className="space-y-2">
            <label className="font-bold text-slate-400 uppercase tracking-widest text-[10px] block">From Start Date</label>
            <div className="relative group">
              <input 
                type="date"
                value={filters.startDate || ''}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full p-2.5 bg-slate-50/50 hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-semibold focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition duration-200 cursor-pointer"
              />
            </div>
          </div>

          {/* End Date */}
          <div className="space-y-2">
            <label className="font-bold text-slate-400 uppercase tracking-widest text-[10px] block">To End Date</label>
            <div className="relative group">
              <input 
                type="date"
                value={filters.endDate || ''}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full p-2.5 bg-slate-50/50 hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-semibold focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition duration-200 cursor-pointer"
              />
            </div>
          </div>

          {/* Month Dropdown Container Mapping Node */}
          <div className="space-y-2">
            <label className="font-bold text-slate-400 uppercase tracking-widest text-[10px] block">Months</label>
            <select
              value={filters.month || ''}
              onChange={(e) => handleFilterChange('month', e.target.value)}
              disabled={!!filters.startDate || !!filters.endDate}
              className="w-full p-2.5 bg-slate-50/50 hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-bold focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed appearance-none"
              style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>")`, backgroundPosition: 'right 10px center', backgroundSize: '16px', backgroundRepeat: 'no-repeat', paddingRight: '32px' }}
            >
              <option value="" className="text-slate-400">Select Months</option>
              {monthsList.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Fiscal Year Assignment Node Block */}
          <div className="space-y-2">
            <label className="font-bold text-slate-400 uppercase tracking-widest text-[10px] block">Target Year</label>
            <select
              value={filters.year || ''}
              onChange={(e) => handleFilterChange('year', e.target.value)}
              disabled={!filters.month}
              className="w-full p-2.5 bg-slate-50/50 hover:bg-slate-50 border border-slate-200 rounded-xl text-slate-800 font-bold focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition duration-200 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed appearance-none"
              style={{ backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='m6 9 6 6 6-6'/></svg>")`, backgroundPosition: 'right 10px center', backgroundSize: '16px', backgroundRepeat: 'no-repeat', paddingRight: '32px' }}
            >
              <option value="">2026</option>
              {yearsList.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* 🟢 MODERNISED ACCENT RED / ROSE ACTION PURGE BUTTON */}
          <div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="w-full flex items-center justify-center gap-2 bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold p-2.5 h-[41px] rounded-xl border border-rose-200/60 text-xs active:scale-[0.98] transition-all duration-200 uppercase tracking-wider shadow-sm shadow-rose-100"
            >
              <RefreshCw className="w-3.5 h-3.5 text-rose-500" /> Reset Filters
            </button>
          </div>
        </div>
        
        {/* Injected Range Lock Callout Banner */}
        {(!!filters.startDate || !!filters.endDate) && (
          <div className="px-5 py-2.5 bg-amber-50/60 border-t border-amber-100 flex items-center gap-2">
            <span className="text-amber-500 text-sm">⚠️</span>
            <p className="text-[11px] font-bold text-amber-700/90 tracking-wide">
              Custom target date ranges are actively blocking monthly drop-down matrix loops. Clear inputs to return to cycle filters.
            </p>
          </div>
        )}
      </div>

      {/* SEGMENT TAB NAVIGATION MATRIX LAYOUT */}
      <div className="flex border-b border-slate-200 gap-1 overflow-x-auto pb-px scrollbar-none">
        {(['all', 'chat', 'call', 'video'] as const).map((tab) => {
          const isActive = filters.sessionType === tab;
          return (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-6 py-3.5 text-sm font-bold border-b-2 capitalize transition-all duration-200 whitespace-nowrap tracking-wide flex items-center gap-2 ${
                isActive
                  ? 'border-blue-600 text-blue-600 bg-white font-extrabold rounded-t-xl shadow-[0_-4px_12px_rgba(0,0,0,0.01)]'
                  : 'border-transparent text-slate-400 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {isActive && <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>}
              {tab} Sessions
            </button>
          );
        })}
      </div>

      {/* TABLE BLOCK FRAME CONSTRUCT */}
      <div className="bg-white rounded-2xl shadow-[0_4px_25px_rgba(15,23,42,0.02)] border border-slate-200/70 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead>
              <tr className="border-b border-slate-200 text-slate-400 text-[10px] font-black bg-slate-50 tracking-widest uppercase">
                <th className="p-4 px-6 w-[70px] text-center">S.NO.</th>
                <th className="p-4">Reference ID</th>
                <th className="p-4">Client Detail</th>
                <th className="p-4">Type</th>
                <th className="p-4"><div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-slate-400" /> Duration</div></th>
                <th className="p-4"><div className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-slate-400" /> Timestamp</div></th>
                <th className="p-4 text-right">User Paid</th>
                <th className="p-4 text-right">PG Fee</th>
                <th className="p-4 text-right">Platform Share</th>
                <th className="p-4 text-right px-6 text-emerald-600 bg-emerald-50/40 font-black">Net Earned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {isLoadingOrders ? (
                <tr>
                  <td colSpan={10} className="p-20 text-center">
                    <Loader2 className="w-9 h-9 animate-spin mx-auto text-blue-500/70" />
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-20 text-center text-slate-400 font-semibold text-sm bg-slate-50/20">
                    <div className="max-w-md mx-auto space-y-2">
                      <p className="text-slate-700 font-bold text-base">No Matching Entries Found</p>
                      <p className="text-xs text-slate-400">We couldn't locate any completed verified billing transactions matching your target sequence layout.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order, idx) => (
                  <tr key={order.id} className="hover:bg-slate-50/60 transition-colors text-sm text-slate-600">
                    
                    {/* S.NO */}
                    <td className="p-4 px-6 text-slate-400 text-xs font-bold text-center">
                      {((filters.page - 1) * filters.limit) + idx + 1}
                    </td>

                    {/* Reference Token ID Tracking Element */}
                    <td className="p-4 text-slate-900 font-mono text-xs font-bold tracking-tight">
                      {order.session_unique_id || order.id.substring(0, 8).toUpperCase()}
                    </td>

                    {/* Client Demographics Node */}
                    <td className="p-4">
                      <p className="font-bold text-slate-800 tracking-tight">{order.user_name || 'Verified Client'}</p>
                      <p className="text-[10px] text-slate-400 font-black uppercase tracking-widest mt-0.5">{order.user_code}</p>
                    </td>

                    {/* Channel Configuration Badge Element */}
                    <td className="p-4">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-50 border border-slate-100 rounded-lg text-slate-700 font-bold capitalize text-xs shadow-sm">
                        {getSessionIcon(order.session_type)}
                        {order.session_type}
                      </div>
                    </td>

                    {/* Duration Display Mapping Frame */}
                    <td className="p-4 text-slate-700 font-semibold font-mono text-xs">
                      {formatDuration(order.duration_seconds)}
                    </td>

                    {/* Datetime Stamp Mapping Layout Frame */}
                    <td className="p-4 text-slate-400 text-xs font-semibold">
                      {new Date(order.started_at).toLocaleDateString('en-IN', {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </td>

                    {/* Base Debit Value Frame */}
                    <td className="p-4 text-right font-bold text-slate-700 font-mono text-xs">
                      ₹{Number(order.user_paid).toFixed(2)}
                    </td>

                    {/* Overhead Gateway Charge Mapping Index Node */}
                    <td className="p-4 text-right text-rose-500/90 font-mono text-xs font-semibold">
                      -₹{Number(order.pg_charges).toFixed(2)}
                    </td>

                    {/* Platform Margin Processing Node */}
                    <td className="p-4 text-right text-slate-400 font-mono text-xs font-semibold">
                      -₹{Number(order.admin_earned).toFixed(2)}
                    </td>

                    {/* Settlement Net Realized Yield Value Node Frame */}
                    <td className="p-4 text-right px-6 font-extrabold text-emerald-600 font-mono bg-emerald-50/20 shadow-inner">
                      +₹{Number(order.expert_earned).toFixed(2)}
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* CONTROLS PAGINATION FOOTER ROW NODE */}
        <div className="p-4 border-t border-slate-200/80 bg-slate-50 flex flex-col sm:flex-row gap-4 items-center justify-between shadow-inner">
          <div className="text-xs text-slate-400 font-bold uppercase tracking-wider">
            Showing <span className="text-slate-700 font-extrabold">{((filters.page - 1) * filters.limit) + 1}</span> to{' '}
            <span className="text-slate-700 font-extrabold">
              {Math.min(filters.page * filters.limit, totalOrders)}
            </span> of <span className="text-slate-800 font-black">{totalOrders}</span> Statement Entries
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={filters.page === 1}
              className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-black bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-40 disabled:hover:bg-white transition active:scale-95 shadow-sm uppercase tracking-wider"
            >
              Prev
            </button>
            <div className="px-3.5 py-1.5 text-xs font-bold text-slate-600 bg-slate-200/50 rounded-lg border border-slate-300/40 shadow-inner font-mono">
              {filters.page} / {Math.max(1, Math.ceil(totalOrders / filters.limit))}
            </div>
            <button
              onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={filters.page >= Math.ceil(totalOrders / filters.limit)}
              className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-black bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-40 disabled:hover:bg-white transition active:scale-95 shadow-sm uppercase tracking-wider"
            >
              Next
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}